import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Sparkles, RefreshCcw, Loader2, Bug, History, Trash2 } from 'lucide-react'
import { Card, CardContent } from '../ui/card'
import { Button } from '../ui/button'
import { RowActionsMenu } from '../ui/RowActionsMenu'
import { useAiCall } from '../../hooks/useAiCall'
import { useAiHistory } from '../../hooks/useAiHistory'
import { useAiDebugEnabled } from '../../hooks/useAiDebugEnabled'
import { useShowAiProvider } from '../../hooks/useShowAiProvider'
import { useAuth } from '../../contexts/AuthContext'
import { useConfirm } from '../../contexts/ConfirmDialogContext'
import { isOwner } from '../../lib/owner'

// Reusable shell for an AI feature card. Handles the button →
// loading → result → refresh / retry / error flow, the auto-save
// to ai_messages via useAiCall, the past-notes history disclosure,
// and the owner-only raw debug panel. The caller just supplies:
//   kind         — string, used both as the ai_messages.kind value
//                  and as the prefix for i18n lookups (ai.<kind>.*).
//   buildPrompt  — async ({ user }) => ({ prompt, generationConfig? }).
//                  Composed once per `run()` so it can read fresh DB
//                  state.
//
// Each AI feature in the app collapses to a 3-line component that
// calls <AiCoachCard kind="..." buildPrompt={...} /> with its
// specific data fetcher + prompt builder.
function formatTimestamp(iso) {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleString('en-CA', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false,
    })
  } catch {
    return iso
  }
}

export default function AiCoachCard({ kind, i18nKey, buildPrompt, historyLimit = 10 }) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [aiDebug] = useAiDebugEnabled()
  const { enabled: showProvider } = useShowAiProvider()
  const confirm = useConfirm()

  // `kind` is the snake_case slug stored in ai_messages.kind so DB
  // history persists across renames. `i18nKey` is the camelCase
  // namespace under `ai.*` for translation lookups. Defaults to
  // `kind` when not supplied so single-word features (where they
  // happen to match) don't need to spell it twice.
  const ns = i18nKey || kind

  const history = useAiHistory(kind, { limit: historyLimit })

  const build = useCallback((opts) => buildPrompt(opts), [buildPrompt])

  const { run, reset, result, loading, error, debug } = useAiCall(build, {
    kind,
    onPersisted: history.refetch,
  })

  const hasResult = !!result?.text
  const showOpen = !hasResult && !loading && !error
  const showDebug = isOwner(user) && aiDebug

  const tk = (key, params) => t(`ai.${ns}.${key}`, params)

  const handleDeleteHistory = async (msg) => {
    const ok = await confirm({
      title: t('ai.history.confirmDelete'),
      confirmLabel: t('common.delete'),
      variant: 'destructive',
      icon: Trash2,
    })
    if (ok) await history.remove(msg.id)
  }

  // De-dupe: if the freshest history row matches what we're currently
  // rendering above (the just-saved generation), skip it in the list.
  const historyToShow = hasResult && history.messages.length > 0
    && history.messages[0].text === result.text
    ? history.messages.slice(1)
    : history.messages

  return (
    <Card>
      <CardContent className="p-3 sm:p-4 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-sm font-semibold flex items-center gap-1.5" dir="auto">
            <Sparkles className="w-4 h-4 text-app-primary-500" />
            {tk('title')}
          </h2>
          {hasResult && (
            <button
              type="button"
              onClick={() => { reset(); run() }}
              disabled={loading}
              aria-label={t('ai.refresh')}
              className="p-1.5 -m-1.5 rounded-md text-muted-foreground hover:text-app-primary-500 hover:bg-accent transition-colors"
            >
              <RefreshCcw className={loading ? 'w-4 h-4 animate-spin' : 'w-4 h-4'} />
            </button>
          )}
        </div>

        <p className="text-xs text-muted-foreground" dir="auto">
          {tk('description')}
        </p>

        {showOpen && (
          <Button onClick={run} className="w-full" size="sm">
            <Sparkles className="w-4 h-4 me-1.5" />
            {tk('cta')}
          </Button>
        )}

        {loading && !hasResult && (
          <div className="flex items-center justify-center gap-2 py-4 text-sm text-muted-foreground" dir="auto">
            <Loader2 className="w-4 h-4 animate-spin" />
            {t('ai.thinking')}
          </div>
        )}

        {error && (
          <div className="p-3 rounded-md bg-red-500/10 ring-1 ring-red-500/30 text-xs text-red-600" dir="auto">
            <p className="font-semibold mb-0.5">{t('ai.errorTitle')}</p>
            <p className="opacity-80">{error}</p>
            <button
              type="button"
              onClick={run}
              className="mt-2 text-app-primary-500 hover:underline"
            >
              {t('ai.retry')}
            </button>
          </div>
        )}

        {hasResult && (
          <div
            className="text-sm text-foreground whitespace-pre-wrap leading-relaxed pt-1"
            dir="auto"
          >
            {result.text}
          </div>
        )}

        {hasResult && result?.model && showProvider && (
          <p className="text-[10px] text-muted-foreground pt-1 font-mono" dir="ltr">
            via {result.model}
          </p>
        )}

        {showDebug && debug && (
          <details className="text-[11px] text-muted-foreground border border-border/60 rounded-md mt-2 bg-muted/30">
            <summary className="cursor-pointer px-2 py-1.5 flex items-center gap-1.5 select-none" dir="auto">
              <Bug className="w-3.5 h-3.5" />
              <span>AI debug (owner)</span>
            </summary>
            <pre
              className="px-2 py-2 text-[10px] font-mono overflow-x-auto whitespace-pre-wrap break-all"
              dir="ltr"
            >
{JSON.stringify(debug, null, 2)}
            </pre>
          </details>
        )}

        {historyToShow.length > 0 && (
          <details className="text-xs border border-border/60 rounded-md mt-2 bg-muted/20">
            <summary className="cursor-pointer px-3 py-2 flex items-center gap-1.5 select-none text-muted-foreground" dir="auto">
              <History className="w-3.5 h-3.5" />
              <span>{t('ai.history.title', { count: historyToShow.length })}</span>
            </summary>
            <ul className="divide-y divide-border/40">
              {historyToShow.map((m) => (
                <li key={m.id} className="p-3">
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] text-muted-foreground font-mono mb-1" dir="ltr">
                        {formatTimestamp(m.created_at)}
                        {m.model && showProvider && <span className="ms-1.5 opacity-80">· {m.model}</span>}
                      </p>
                      <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed" dir="auto">
                        {m.text}
                      </p>
                    </div>
                    <RowActionsMenu
                      ariaLabel={t('common.actions')}
                      actions={[
                        {
                          icon: Trash2,
                          label: t('common.delete'),
                          onClick: () => handleDeleteHistory(m),
                          variant: 'destructive',
                        },
                      ]}
                    />
                  </div>
                </li>
              ))}
            </ul>
          </details>
        )}

        {history.tableMissing && (
          <p className="text-[10px] text-amber-700 dark:text-amber-400" dir="auto">
            {t('ai.history.tableMissing')}
          </p>
        )}
      </CardContent>
    </Card>
  )
}
